import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Button, Card, Input, Select, Space, Typography, message } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { type CSSProperties, type ReactNode, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    GridItem as ZhGridItem,
    Table as ZhTable,
    type TableColumnProps,
    Upload as ZhUpload,
    type UploadRequestOptions,
    type UploadUserFile
} from "zhihao-ui-react";

import request, { type CustomAxiosRequestConfig } from "@/api";
import { DEFAULT_VALUE } from "@/config";
import Permission from "@/views/components/Permission";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import DetailJson, { type DetailJsonBlock } from "@/views/components/detailJson";
import { useUserStore } from "@/stores/react/authStore";
import { confirmDialog } from "@/utils/confirmDialog";

export interface InstitutionApplyRecord {
    institutionApply?: {
        id?: number;
        institutionName?: string;
        unifiedSocialCreditCode?: string;
        legalRepresentative?: string;
        businessLicenseFileId?: number;
        powerAttorneyFileId?: number;
        state?: number | string;
        createdTime?: string;
        approveName?: string;
        approveTime?: string;
    };
    customer?: { code?: string; mobile?: string; realName?: string; updatedTime?: string } | null;
}

export interface InstitutionApplyFile {
    id?: number;
    path?: string;
}

export interface InstitutionApplyQuery {
    page: number;
    size: number;
    institutionName?: string;
    state?: number;
    sortKey?: string;
    sortOrder?: string;
}

export interface InstitutionApplyPageProps {
    loadApplications?: (query: InstitutionApplyQuery) => Promise<{ list?: InstitutionApplyRecord[]; total?: number }>;
    navigateTo?: (mode: "detail" | "audit" | "edit", id: number) => void;
}

export interface InstitutionApplyDetailPageProps {
    id?: number;
    auditMode?: boolean;
    editMode?: boolean;
    loadDetail?: (id: number) => Promise<InstitutionApplyRecord>;
    getFile?: (id: number) => Promise<InstitutionApplyFile>;
    approve?: (id: number, payload: { state: number; remark: string }) => Promise<unknown>;
    submit?: (id: number, payload: InstitutionApplyForm) => Promise<unknown>;
    uploadFile?: (file: File) => Promise<InstitutionApplyFile>;
    ocrBusinessLicense?: (
        fileId: number
    ) => Promise<Pick<InstitutionApplyForm, "institutionName" | "unifiedSocialCreditCode" | "legalRepresentative">>;
}

export interface InstitutionApplyForm {
    institutionName?: string;
    unifiedSocialCreditCode?: string;
    businessLicenseFileId?: number;
    powerAttorneyFileId?: number;
    legalRepresentative?: string;
}

const stateLabels: Record<number, string> = { 1: "待审核", 2: "已驳回", 3: "已通过" };
const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);
const institutionUploadTriggerSizeStyle: CSSProperties = {
    boxSizing: "border-box",
    width: 232,
    minWidth: 232,
    height: 123,
    flex: "0 0 232px"
};
// 空态保持旧 Vue el-upload-dragger 的淡蓝透明底，预览态保持 img-list 的实色底。
const institutionUploadEmptyTriggerStyle: CSSProperties = {
    ...institutionUploadTriggerSizeStyle,
    backgroundColor: "rgba(232, 242, 255, 0.2)"
};
const institutionUploadPreviewStyle: CSSProperties = {
    ...institutionUploadTriggerSizeStyle,
    backgroundColor: "#e8efff"
};

/** 复用 Vue `/admin/institution-apply/paging` 认证审核分页接口。 */
export async function loadInstitutionApplications(
    query: InstitutionApplyQuery
): Promise<{ list?: InstitutionApplyRecord[]; total?: number }> {
    const response = await request.service.get("/admin/institution-apply/paging", { params: query });
    return response.data;
}

/** 复用 Vue `/admin/institution-apply/vo/:id` 认证详情接口。 */
export async function loadInstitutionApplyDetail(id: number): Promise<InstitutionApplyRecord> {
    const response = await request.service.get(`/admin/institution-apply/vo/${id}`);
    return response.data;
}

/** 复用 Vue 文件详情接口，为详情图片和修改表单提供文件地址。 */
export async function loadInstitutionApplyFile(id: number): Promise<InstitutionApplyFile> {
    // 文件预览是详情页的辅助请求，文件已被清理时沿用 Vue 空上传态，不打断主页面并弹出全局错误。
    const response = await request.service.get(`/dataService/admin/file/${id}`, {
        suppressGlobalError: true
    } as CustomAxiosRequestConfig);
    return response.data;
}

/** 复用 Vue UploadList 使用的文件上传接口，页面只保留文件 ID 契约。 */
export async function uploadInstitutionApplyFile(file: File): Promise<InstitutionApplyFile> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await request.service.post("/dataService/admin/file/upload", formData, { timeout: 0 });
    return response.data;
}

/** 营业执照上传成功后沿用 Vue OCR 自动回填认证字段。 */
export async function ocrInstitutionApplyBusinessLicense(
    fileId: number
): Promise<Pick<InstitutionApplyForm, "institutionName" | "unifiedSocialCreditCode" | "legalRepresentative">> {
    const response = await request.service.get(`/admin/ocr/businessLicense?fileId=${fileId}`);
    return response.data;
}

/** 保留 Vue 审核状态与备注 payload。 */
export async function approveInstitutionApply(
    id: number,
    payload: { state: number; remark: string }
): Promise<unknown> {
    const response = await request.service.put(`/admin/institution-apply/approve/${id}`, payload);
    return response.data;
}

/** 保留 Vue 修改认证字段和文件 ID 的提交契约。 */
export async function submitInstitutionApply(id: number, payload: InstitutionApplyForm): Promise<unknown> {
    const response = await request.service.put(`/admin/institution-apply/${id}`, payload);
    return response.data;
}

export function InstitutionApplyPage({
    loadApplications = loadInstitutionApplications,
    navigateTo
}: InstitutionApplyPageProps) {
    const navigate = useNavigate();
    const banButton = useUserStore(user => user.banButton);
    const [applications, setApplications] = useState<InstitutionApplyRecord[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<InstitutionApplyQuery>({ page: 1, size: 10 });
    const [institutionName, setInstitutionName] = useState("");
    const [state, setState] = useState<number | undefined>();
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        let disposed = false;
        void loadApplications(query)
            .then(result => {
                if (disposed) return;
                setApplications(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            })
            .catch(() => {
                if (disposed) return;
                setApplications([]);
                setTotal(0);
                setErrorMessage("企业认证列表加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadApplications, query]);

    const go = (mode: "detail" | "audit" | "edit", id?: number) => {
        if (id == null) return;
        if (navigateTo) {
            navigateTo(mode, id);
            return;
        }
        const path = mode === "detail" ? `info/${id}` : mode === "audit" ? `audit/${id}` : `form/${id}`;
        navigate(`/lianYunUser/institutionApply/${path}`);
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 认证审核列表的字段和权限操作插槽。
    const columns: TableColumnProps<InstitutionApplyRecord>[] = [
        {
            key: "id",
            label: "编号",
            width: 148,
            prop: "institutionApply.id",
            render: ({ row }) => (
                <Permission anyOf={["ROLE_SS_ORG_REVIEW"]}>
                    <Button
                        type="link"
                        aria-label={`查看 ${row.institutionApply?.id}`}
                        onClick={() => go("detail", row.institutionApply?.id)}
                    >
                        {row.institutionApply?.id ?? DEFAULT_VALUE}
                    </Button>
                </Permission>
            )
        },
        {
            key: "realName",
            label: "个人实名",
            width: 148,
            prop: "customer.realName",
            render: ({ row }) => row.customer?.realName || DEFAULT_VALUE
        },
        {
            key: "institutionName",
            label: "企业名称",
            minWidth: 350,
            prop: "institutionApply.institutionName",
            render: ({ row }) => row.institutionApply?.institutionName || DEFAULT_VALUE
        },
        {
            key: "createdTime",
            label: "创建时间",
            width: 176,
            prop: "institutionApply.created_time",
            sortable: true,
            render: ({ row }) => formatDate(row.institutionApply?.createdTime)
        },
        {
            key: "approveName",
            label: "审核人",
            width: 148,
            prop: "institutionApply.approveName",
            render: ({ row }) => row.institutionApply?.approveName || DEFAULT_VALUE
        },
        {
            key: "approveTime",
            label: "审核时间",
            width: 176,
            prop: "institutionApply.approve_time",
            sortable: true,
            render: ({ row }) => formatDate(row.institutionApply?.approveTime)
        },
        {
            key: "state",
            label: "状态",
            width: 106,
            prop: "institutionApply.state",
            render: ({ row }) => stateLabels[Number(row.institutionApply?.state)] || DEFAULT_VALUE
        },
        {
            key: "operation",
            label: "操作",
            fixed: "right",
            // 对齐 Vue `TABLE_COLUMN_WIDTH.WORD4`，避免操作列压缩审批入口。
            width: 92,
            prop: "institutionApply",
            render: ({ row }) => {
                const id = row.institutionApply?.id;
                const currentState = Number(row.institutionApply?.state);
                return (
                    <Space>
                        {currentState === 1 ? (
                            <Permission anyOf={["ROLE_SS_ORG_DEAL"]}>
                                <Button
                                    type="link"
                                    aria-label={`审核 ${id}`}
                                    disabled={banButton}
                                    onClick={() => go("audit", id)}
                                >
                                    审核
                                </Button>
                            </Permission>
                        ) : null}
                        {currentState === 3 ? (
                            <Permission anyOf={["ROLE_SS_ORG_REVIEW"]}>
                                <Button
                                    type="link"
                                    aria-label={`修改 ${id}`}
                                    disabled={banButton}
                                    onClick={() => go("edit", id)}
                                >
                                    修改
                                </Button>
                            </Permission>
                        ) : null}
                    </Space>
                );
            }
        }
    ];

    // 将 zhihao-ui-react 的列字段映射回 Vue 分页接口的 snake_case 排序参数。
    const handleSortChange = ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
        if (prop !== "institutionApply.created_time" && prop !== "institutionApply.approve_time") return;
        setQuery(current => {
            const nextQuery = { ...current, page: 1 };
            if (!order) {
                delete nextQuery.sortKey;
                delete nextQuery.sortOrder;
            } else {
                nextQuery.sortKey = prop.endsWith("created_time") ? "created_time" : "approve_time";
                nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
            }
            return nextQuery;
        });
    };

    return (
        <section aria-label="institution-apply-page" className="institution-apply-page flex h-full min-h-full flex-col">
            <Card>
                <ZhTable<InstitutionApplyRecord>
                    data={applications}
                    columns={columns}
                    pagination={false}
                    rowKey={row => String(row.institutionApply?.id || "")}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                企业认证审核
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Space wrap className="admin-table-filter-form mb-4">
                                <label>
                                    企业名称
                                    <Input
                                        aria-label="企业名称"
                                        maxLength={50}
                                        placeholder="请输入企业名称关键字搜索"
                                        value={institutionName}
                                        onChange={event => setInstitutionName(event.target.value)}
                                    />
                                </label>
                                <label>
                                    认证状态
                                    <Select
                                        allowClear
                                        aria-label="认证状态"
                                        options={Object.entries(stateLabels).map(([value, label]) => ({
                                            label,
                                            value: Number(value)
                                        }))}
                                        placeholder="请选择状态"
                                        value={state}
                                        onChange={setState}
                                    />
                                </label>
                                <Button
                                    aria-label="查询"
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                    onClick={() =>
                                        setQuery({
                                            page: 1,
                                            size: query.size,
                                            ...(institutionName ? { institutionName } : {}),
                                            ...(state ? { state } : {})
                                        })
                                    }
                                >
                                    搜索
                                </Button>
                                <Button
                                    aria-label="重置"
                                    className="table-filter-reset-button"
                                    icon={<TableSearchIcon name="reset" />}
                                    title="重置"
                                    onClick={() => {
                                        setInstitutionName("");
                                        setState(undefined);
                                        setQuery(current => ({ page: 1, size: current.size }));
                                    }}
                                />
                            </Space>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                // 对齐旧 Vue Pagination 的 sizes，并在条数变化时重置到第一页。
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

/** 认证详情、审核和修改共用 Vue 历史页面的数据与操作契约。 */
export function InstitutionApplyDetailPage({
    id: providedId,
    auditMode = false,
    editMode = false,
    loadDetail = loadInstitutionApplyDetail,
    getFile = loadInstitutionApplyFile,
    approve = approveInstitutionApply,
    submit = submitInstitutionApply,
    uploadFile = uploadInstitutionApplyFile,
    ocrBusinessLicense = ocrInstitutionApplyBusinessLicense
}: InstitutionApplyDetailPageProps) {
    const routeParams = useParams();
    const navigate = useNavigate();
    const banButton = useUserStore(user => user.banButton);
    const id = providedId ?? (routeParams.id ? Number(routeParams.id) : undefined);
    const [detail, setDetail] = useState<InstitutionApplyRecord>();
    const [businessLicenseFile, setBusinessLicenseFile] = useState<InstitutionApplyFile>();
    const [powerAttorneyFile, setPowerAttorneyFile] = useState<InstitutionApplyFile>();
    const [businessLicenseUploadList, setBusinessLicenseUploadList] = useState<UploadUserFile[]>([]);
    const [powerAttorneyUploadList, setPowerAttorneyUploadList] = useState<UploadUserFile[]>([]);
    const [loadingError, setLoadingError] = useState("");
    const [formError, setFormError] = useState("");
    const [actionError, setActionError] = useState("");
    const [auditState, setAuditState] = useState<number>();
    const [auditRemark, setAuditRemark] = useState("");
    const [auditError, setAuditError] = useState("");
    const [form, setForm] = useState<InstitutionApplyForm>({});

    useEffect(() => {
        if (id == null) return;
        let disposed = false;
        void loadDetail(id)
            .then(result => {
                if (disposed) return;
                setDetail(result);
                setForm({
                    institutionName: result.institutionApply?.institutionName,
                    unifiedSocialCreditCode: result.institutionApply?.unifiedSocialCreditCode,
                    businessLicenseFileId: result.institutionApply?.businessLicenseFileId,
                    powerAttorneyFileId: result.institutionApply?.powerAttorneyFileId,
                    legalRepresentative: result.institutionApply?.legalRepresentative
                });
                setLoadingError("");
                const fileIds = [
                    result.institutionApply?.businessLicenseFileId,
                    result.institutionApply?.powerAttorneyFileId
                ];
                if (fileIds[0] != null) {
                    void getFile(fileIds[0])
                        .then(file => {
                            if (!disposed) {
                                setBusinessLicenseFile(file);
                                setBusinessLicenseUploadList([
                                    { name: "营业执照", status: "success", uid: -fileIds[0]!, url: file.path }
                                ]);
                            }
                        })
                        .catch(() => undefined);
                }
                if (fileIds[1] != null) {
                    void getFile(fileIds[1])
                        .then(file => {
                            if (!disposed) {
                                setPowerAttorneyFile(file);
                                setPowerAttorneyUploadList([
                                    { name: "委托授权书", status: "success", uid: -fileIds[1]!, url: file.path }
                                ]);
                            }
                        })
                        .catch(() => undefined);
                }
            })
            .catch(() => {
                if (!disposed) setLoadingError("企业认证详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [getFile, id, loadDetail]);

    const currentId = detail?.institutionApply?.id ?? id;
    const currentState = Number(detail?.institutionApply?.state);
    const closeAuditDialog = () => {
        setAuditState(undefined);
        setAuditRemark("");
        setAuditError("");
    };
    const openAuditDialog = (state: number) => {
        setAuditState(state);
        setAuditRemark("");
        setAuditError("");
    };
    const updateForm = (key: keyof InstitutionApplyForm, value: string) => {
        setForm(current => ({ ...current, [key]: value }));
        setFormError("");
    };

    const handleSubmit = async () => {
        if (currentId == null) return;
        setFormError("");
        if (!form.institutionName?.trim()) {
            setFormError("请输入组织机构名称");
            return;
        }
        if (!form.unifiedSocialCreditCode || !/^[a-zA-Z0-9]{18}$/.test(form.unifiedSocialCreditCode)) {
            setFormError("统一社会信用代码需为18位数字和字母");
            return;
        }
        if (!form.legalRepresentative?.trim()) {
            setFormError("请输入法人代表");
            return;
        }
        if (form.businessLicenseFileId == null) {
            setFormError("请上传营业执照");
            return;
        }
        // 沿用旧 Vue 修改页的二次提交确认，取消时不触发保存接口或返回导航。
        if (!(await confirmDialog("是否提交", "提示"))) return;
        try {
            await submit(currentId, form);
            message.success("提交成功");
            setFormError("");
            // 修改页提交成功后沿用 Vue router.back，避免停留在编辑表单并保留错误页面标题。
            navigate(-1);
        } catch {
            setFormError("企业认证保存失败");
        }
    };

    const handleAudit = async () => {
        if (currentId == null || auditState == null) return;
        // 旧 Vue 审核弹窗只要求驳回意见，通过时保留可选备注。
        if (auditState === 2 && !auditRemark.trim()) {
            setAuditError("请输入审核意见");
            return;
        }
        setActionError("");
        setAuditError("");
        try {
            await approve(currentId, { state: auditState, remark: auditRemark });
            message.success(auditState === 3 ? "已通过" : "已拒绝");
            closeAuditDialog();
            navigate("/lianYunUser/institutionApply?refresh=true");
        } catch {
            setActionError("企业认证审核失败");
        }
    };

    // 对齐 Vue UploadList 的图片类型和 10MB 限制，避免把非图片或超大文件交给接口。
    const beforeImageUpload = (file: File) => {
        const acceptedTypes = ["image/jpeg", "image/png", "image/webp", "image/avif"];
        if (!acceptedTypes.includes(file.type)) {
            setFormError("仅支持 JPG、PNG、WEBP、AVIF 图片");
            return false;
        }
        if (file.size > 10 * 1024 * 1024) {
            setFormError("图片大小不能超过10MB");
            return false;
        }
        return true;
    };

    // 自定义上传成功后只写入文件 ID；营业执照额外触发 Vue 兼容 OCR 回填。
    const uploadImage = async (
        options: UploadRequestOptions,
        kind: "businessLicense" | "powerAttorney"
    ): Promise<void> => {
        try {
            const uploaded = await uploadFile(options.file);
            const fileId = Number(uploaded.id);
            if (!fileId) throw new Error("missing file id");
            const uploadItem: UploadUserFile = {
                name: options.file.name,
                status: "success",
                uid: Date.now(),
                url: uploaded.path
            };
            if (kind === "businessLicense") {
                setBusinessLicenseUploadList([uploadItem]);
                setBusinessLicenseFile(uploaded);
                setForm(current => ({ ...current, businessLicenseFileId: fileId }));
                const ocrResult = await ocrBusinessLicense(fileId);
                setForm(current => ({ ...current, ...ocrResult, businessLicenseFileId: fileId }));
            } else {
                setPowerAttorneyUploadList([uploadItem]);
                setPowerAttorneyFile(uploaded);
                setForm(current => ({ ...current, powerAttorneyFileId: fileId }));
            }
            options.onSuccess(uploaded);
            setFormError("");
        } catch (error) {
            options.onError(error instanceof Error ? error : new Error("upload failed"));
        }
    };

    // 文件区域复用旧 Vue DetailJson 的 rowSlot：标题、固定尺寸预览和空图片占位保持一致。
    const renderFile = (label: string, file?: InstitutionApplyFile) => (
        <div className="institution-apply-file-field">
            <div className="institution-apply-file-title">{label}</div>
            {file?.path ? (
                <img alt={label} className="institution-apply-file-preview" src={file.path} />
            ) : (
                <div className="institution-apply-file-placeholder">暂无图片</div>
            )}
        </div>
    );

    // 自定义触发槽复用 Vue 的图标空态，并在有文件时继续支持替换与删除。
    const renderUploadTrigger = (label: string, file: InstitutionApplyFile | undefined, onRemove: () => void) => (
        <span
            className={file?.path ? "institution-apply-upload-preview" : "institution-apply-empty-file-slot"}
            style={file?.path ? institutionUploadPreviewStyle : institutionUploadEmptyTriggerStyle}
        >
            {file?.path ? (
                <>
                    <img
                        alt={label}
                        className="admin-upload-preview-media institution-apply-upload-preview__image"
                        src={file.path}
                    />
                    <span aria-hidden="true" className="institution-apply-upload-preview__mask">
                        替换图片
                    </span>
                    <button
                        aria-label={`移除${label}`}
                        className="institution-apply-upload-preview__remove"
                        type="button"
                        onClick={event => {
                            // 上传组件会把触发节点的点击当作重新选图；删除只清理关联状态。
                            event.stopPropagation();
                            onRemove();
                        }}
                    >
                        ×
                    </button>
                </>
            ) : (
                <>
                    <i aria-hidden="true" className="iconfont icon-shangchuan" />
                    <span className="institution-apply-empty-file-slot__prompt">上传图片</span>
                </>
            )}
        </span>
    );

    // 只读和审核页统一使用 Vue 同源 DetailJson 模板，避免业务页手写标题与网格再次漂移。
    const detailColumns: DetailJsonBlock[] = [
        {
            sectionTitle: "账号信息",
            list: [
                { label: "用户ID", value: () => detail?.customer?.code || DEFAULT_VALUE },
                { label: "注册手机号", value: () => detail?.customer?.mobile || DEFAULT_VALUE },
                { label: "个人实名", value: () => detail?.customer?.realName || DEFAULT_VALUE }
            ]
        },
        {
            sectionTitle: "认证信息",
            list: [
                {
                    label: "组织机构名称",
                    value: () => detail?.institutionApply?.institutionName || DEFAULT_VALUE
                },
                {
                    label: "统一社会信用代码",
                    value: () => detail?.institutionApply?.unifiedSocialCreditCode || DEFAULT_VALUE
                },
                {
                    label: "法人代表",
                    span: 2,
                    value: () => detail?.institutionApply?.legalRepresentative || DEFAULT_VALUE
                },
                { rowSlot: "businessLicenseSlot" },
                { rowSlot: "powerAttorneySlot" }
            ]
        },
        {
            sectionTitle: "审核信息",
            show: currentState !== 1,
            list: [
                { label: "结果", value: () => stateLabels[currentState] || DEFAULT_VALUE },
                { label: "审核人", value: () => detail?.institutionApply?.approveName || DEFAULT_VALUE },
                { label: "时间", value: () => formatDate(detail?.institutionApply?.approveTime) }
            ]
        }
    ];

    const legacyFieldText = (label: string, value: string | number | undefined): ReactNode => (
        <span className="sr-only">{`${label}：${value || DEFAULT_VALUE}`}</span>
    );

    // 详情页统一壳层契约：className="detail-json-page detail-page-transparent admin-detail-page"
    return (
        <section
            aria-label="institution-apply-detail-page"
            className={
                editMode
                    ? "detail-json-page detail-page-transparent admin-detail-page institution-apply-edit-page"
                    : "detail-json-page detail-page-transparent admin-detail-page"
            }
        >
            <div className="section-card admin-detail-header-card">
                <ZhDetailHeader
                    leftInfos={[{ label: "编号", value: currentId ?? DEFAULT_VALUE }]}
                    rightInfos={[
                        {
                            label: "创建时间",
                            value: formatDate(detail?.customer?.updatedTime || detail?.institutionApply?.createdTime)
                        }
                    ]}
                    stateDesc={stateLabels[currentState] || DEFAULT_VALUE}
                    title={auditMode ? "审核企业认证" : editMode ? "机构认证详情" : "企业认证详情"}
                />
            </div>
            {loadingError ? <Alert className="mb-4" message={loadingError} role="alert" type="error" /> : null}
            {actionError ? <Alert className="mb-4" message={actionError} role="alert" type="error" /> : null}
            {!editMode ? (
                <>
                    <DetailJson
                        columns={detailColumns}
                        slots={{
                            businessLicenseSlot: () => renderFile("营业执照", businessLicenseFile),
                            powerAttorneySlot: () => renderFile("委托授权书", powerAttorneyFile)
                        }}
                    />
                    {legacyFieldText("用户ID", detail?.customer?.code)}
                    {legacyFieldText("组织机构名称", detail?.institutionApply?.institutionName)}
                    {legacyFieldText("统一社会信用代码", detail?.institutionApply?.unifiedSocialCreditCode)}
                </>
            ) : (
                <div className="detail-json">
                    <section className="detail-json__block institution-apply-account-block">
                        <div className="section-title">账号信息</div>
                        <ZhGrid className="admin-detail-grid institution-apply-account-grid" cols={4} gap={[32, 0]}>
                            <ZhGridItem>
                                <ZhBaseItem label="用户ID" value={detail?.customer?.code || DEFAULT_VALUE} />
                            </ZhGridItem>
                            <ZhGridItem>
                                <ZhBaseItem label="注册手机号" value={detail?.customer?.mobile || DEFAULT_VALUE} />
                            </ZhGridItem>
                            <ZhGridItem>
                                <ZhBaseItem label="个人实名" value={detail?.customer?.realName || DEFAULT_VALUE} />
                            </ZhGridItem>
                        </ZhGrid>
                    </section>
                    <section className="detail-json__block institution-apply-certification-block">
                        <div className="section-title">认证信息</div>
                        <ZhGrid className="admin-detail-grid institution-apply-form-grid" cols={4} gap={[32, 0]}>
                            <ZhGridItem>
                                <label className="institution-apply-form-item">
                                    组织机构名称
                                    <Input
                                        aria-label="组织机构名称"
                                        value={form.institutionName || ""}
                                        onChange={event => updateForm("institutionName", event.target.value)}
                                    />
                                </label>
                            </ZhGridItem>
                            <ZhGridItem>
                                <label className="institution-apply-form-item">
                                    统一社会信用代码
                                    <Input
                                        aria-label="统一社会信用代码"
                                        value={form.unifiedSocialCreditCode || ""}
                                        onChange={event => updateForm("unifiedSocialCreditCode", event.target.value)}
                                    />
                                </label>
                            </ZhGridItem>
                            <ZhGridItem>
                                <label className="institution-apply-form-item">
                                    法人代表
                                    <Input
                                        aria-label="法人代表"
                                        value={form.legalRepresentative || ""}
                                        onChange={event => updateForm("legalRepresentative", event.target.value)}
                                    />
                                </label>
                            </ZhGridItem>
                        </ZhGrid>
                        <ZhGrid className="admin-detail-grid institution-apply-file-grid" cols={4} gap={[32, 0]}>
                            <ZhGridItem>
                                <div className="institution-apply-file-item">
                                    <span className="institution-apply-file-item__title">营业执照</span>
                                    <ZhUpload
                                        action=""
                                        accept="image/jpeg,image/png,image/webp,image/avif"
                                        className="institution-business-license-upload"
                                        fileList={businessLicenseUploadList}
                                        limit={1}
                                        listType="picture-card"
                                        showFileList={false}
                                        beforeUpload={beforeImageUpload}
                                        httpRequest={options => void uploadImage(options, "businessLicense")}
                                        onRemove={() => {
                                            setBusinessLicenseUploadList([]);
                                            setBusinessLicenseFile(undefined);
                                            setForm(current => ({ ...current, businessLicenseFileId: undefined }));
                                        }}
                                    >
                                        {renderUploadTrigger("营业执照", businessLicenseFile, () => {
                                            setBusinessLicenseUploadList([]);
                                            setBusinessLicenseFile(undefined);
                                            setForm(current => ({ ...current, businessLicenseFileId: undefined }));
                                        })}
                                    </ZhUpload>
                                    <input
                                        aria-label="营业执照文件ID"
                                        className="sr-only"
                                        type="number"
                                        value={form.businessLicenseFileId ?? ""}
                                        readOnly
                                    />
                                </div>
                            </ZhGridItem>
                            <ZhGridItem>
                                <div className="institution-apply-file-item">
                                    <span className="institution-apply-file-item__title">委托授权书</span>
                                    <ZhUpload
                                        action=""
                                        accept="image/jpeg,image/png,image/webp,image/avif"
                                        className="institution-power-attorney-upload"
                                        fileList={powerAttorneyUploadList}
                                        limit={1}
                                        listType="picture-card"
                                        showFileList={false}
                                        beforeUpload={beforeImageUpload}
                                        httpRequest={options => void uploadImage(options, "powerAttorney")}
                                        onRemove={() => {
                                            setPowerAttorneyUploadList([]);
                                            setPowerAttorneyFile(undefined);
                                            setForm(current => ({ ...current, powerAttorneyFileId: undefined }));
                                        }}
                                    >
                                        {renderUploadTrigger("委托授权书", powerAttorneyFile, () => {
                                            setPowerAttorneyUploadList([]);
                                            setPowerAttorneyFile(undefined);
                                            setForm(current => ({ ...current, powerAttorneyFileId: undefined }));
                                        })}
                                    </ZhUpload>
                                    <input
                                        aria-label="委托授权书文件ID"
                                        className="sr-only"
                                        type="number"
                                        value={form.powerAttorneyFileId ?? ""}
                                        readOnly
                                    />
                                </div>
                            </ZhGridItem>
                        </ZhGrid>
                        {formError ? <Alert className="mt-4" message={formError} role="alert" type="error" /> : null}
                    </section>
                </div>
            )}
            <Space className="operation-fixed-btn" size={0}>
                {editMode ? (
                    <Permission anyOf={["ROLE_SS_ORG_DEAL"]}>
                        <Button
                            aria-label="保存企业认证"
                            disabled={banButton}
                            type="primary"
                            onClick={() => void handleSubmit()}
                        >
                            提交
                        </Button>
                    </Permission>
                ) : null}
                {auditMode ? (
                    <>
                        <Permission anyOf={["ROLE_SS_ORG_DEAL"]}>
                            <Button disabled={banButton} type="primary" onClick={() => openAuditDialog(3)}>
                                同意
                            </Button>
                        </Permission>
                        <Permission anyOf={["ROLE_SS_ORG_DEAL"]}>
                            <Button disabled={banButton} onClick={() => openAuditDialog(2)}>
                                驳回
                            </Button>
                        </Permission>
                    </>
                ) : null}
                {!auditMode && !editMode && currentState === 1 ? (
                    <Permission anyOf={["ROLE_SS_ORG_DEAL"]}>
                        <Button
                            disabled={banButton}
                            onClick={() => navigate(`/lianYunUser/institutionApply/audit/${currentId}`)}
                        >
                            审核
                        </Button>
                    </Permission>
                ) : null}
                {!auditMode && !editMode && currentState === 3 ? (
                    <Permission anyOf={["ROLE_SS_ORG_REVIEW"]}>
                        <Button
                            disabled={banButton}
                            onClick={() => navigate(`/lianYunUser/institutionApply/form/${currentId}`)}
                        >
                            修改
                        </Button>
                    </Permission>
                ) : null}
                <Button disabled={banButton} onClick={() => navigate(-1)}>
                    返回
                </Button>
            </Space>
            <ZhDialog
                open={auditState != null}
                title={auditState === 3 ? "审核通过" : "审核驳回"}
                modalClass="institution-apply-audit-dialog"
                width={544}
                alignCenter
                onCancel={closeAuditDialog}
                footer={
                    <Space>
                        <Button onClick={closeAuditDialog}>取消</Button>
                        <Button type="primary" onClick={() => void handleAudit()}>
                            确定
                        </Button>
                    </Space>
                }
            >
                <div className="institution-apply-audit-form">
                    <label htmlFor="institution-apply-audit-remark">
                        {auditState === 2 ? <span className="institution-apply-audit-form__required">*</span> : null}
                        审核意见
                    </label>
                    <div className="institution-apply-audit-form__control">
                        <Input.TextArea
                            id="institution-apply-audit-remark"
                            aria-label="审核意见"
                            className="institution-apply-audit-form__textarea"
                            maxLength={100}
                            placeholder="请输入审核意见"
                            rows={8}
                            showCount
                            styles={{
                                textarea: { height: 120, paddingBottom: 20 },
                                count: { bottom: 8, right: 8 }
                            }}
                            value={auditRemark}
                            onChange={event => {
                                setAuditRemark(event.target.value);
                                setAuditError("");
                            }}
                        />
                        {auditError ? (
                            <div className="institution-apply-audit-form__error" role="alert">
                                {auditError}
                            </div>
                        ) : null}
                    </div>
                </div>
            </ZhDialog>
        </section>
    );
}
